import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useSearch } from 'wouter';
import { rememberListUrl } from '@/lib/listMemory';
import {
  defaultUrlState,
  isDefaultValue,
  mergeUrlState,
  parseUrlState,
  serializeUrlState,
  type UrlSchema,
  type UrlStateOf,
} from '@/lib/urlState';

const DEFAULT_VIEW_KEYS = [
  'sortBy',
  'sortOrder',
  'sort',
  'order',
  'limit',
  'pageSize',
  'tab',
  'view',
];

export interface UrlStateOptions<S extends UrlSchema> {
  memoryKey?: string;
  pageKey?: keyof S & string;
  viewKeys?: (keyof S & string)[];
}

export type SetUrlState<S extends UrlSchema> = (
  patch: Partial<UrlStateOf<S>>,
  options?: { resetPage?: boolean },
) => void;

export function useUrlState<S extends UrlSchema>(
  schema: S,
  options: UrlStateOptions<S> = {},
) {
  const search = useSearch();
  const [, setLocation] = useLocation();
  const [path] = useState(() => window.location.pathname);
  const keys = Object.keys(schema) as (keyof S & string)[];
  const pageKey =
    options.pageKey ??
    (('page' in schema ? 'page' : undefined) as (keyof S & string) | undefined);
  const viewKeys =
    options.viewKeys ?? keys.filter((key) => DEFAULT_VIEW_KEYS.includes(key));
  const viewKeysId = viewKeys.join(',');
  const memoryKey = options.memoryKey ?? path;

  const state = useMemo(() => parseUrlState(schema, search), [schema, search]);

  useEffect(() => {
    if (window.location.pathname !== path) return;
    rememberListUrl(memoryKey, search ? `${path}?${search}` : path);
  }, [memoryKey, path, search]);

  const write = useCallback(
    (next: UrlStateOf<S>) => {
      if (window.location.pathname !== path) return;
      const current = window.location.search.replace(/^\?/, '');
      const qs = serializeUrlState(schema, next, current);
      if (qs === current) return;
      setLocation(`~${path}${qs ? `?${qs}` : ''}`, { replace: true });
    },
    [schema, path, setLocation],
  );

  const setState = useCallback<SetUrlState<S>>(
    (patch, opts) => {
      const current = parseUrlState(schema, window.location.search);
      write(
        mergeUrlState(schema, current, patch, pageKey, opts?.resetPage ?? true),
      );
    },
    [schema, pageKey, write],
  );

  const clear = useCallback(() => {
    cancelUrlDrafts();
    const current = parseUrlState(schema, window.location.search);
    const next = defaultUrlState(schema);
    for (const key of viewKeysId.split(',')) {
      if (key && key in schema) next[key as keyof S] = current[key];
    }
    write(next);
  }, [schema, viewKeysId, write]);

  const isFiltered = keys.some(
    (key) =>
      key !== pageKey &&
      !viewKeys.includes(key) &&
      !isDefaultValue(schema[key], state[key]),
  );

  return [state, setState, { clear, isFiltered }] as const;
}

const draftCancels = new Set<() => void>();
let navGeneration = 0;

export function cancelUrlDrafts() {
  for (const cancel of [...draftCancels]) cancel();
}

if (typeof window !== 'undefined') {
  for (const type of ['popstate', 'pushState']) {
    window.addEventListener(type, () => {
      navGeneration++;
      cancelUrlDrafts();
    });
  }
}

export function useUrlDraft<T>(
  value: T,
  commit: (next: T) => void,
  options: { delay?: number; normalize?: (next: T) => T } = {},
) {
  const { delay = 300 } = options;
  const [draft, setDraftState] = useState(value);
  const [epoch, setEpoch] = useState(0);
  const sent = useRef(JSON.stringify(value));
  const timer = useRef<number | undefined>(undefined);
  const latest = useRef({ value, commit, normalize: options.normalize });

  useEffect(() => {
    latest.current = { value, commit, normalize: options.normalize };
  });

  const valueId = JSON.stringify(value);
  useEffect(() => {
    if (valueId === sent.current) return;
    window.clearTimeout(timer.current);
    timer.current = undefined;
    sent.current = valueId;
    setDraftState(latest.current.value);
  }, [valueId]);

  useEffect(() => {
    if (epoch === 0) return;
    sent.current = JSON.stringify(latest.current.value);
    setDraftState(latest.current.value);
  }, [epoch]);

  useEffect(() => {
    const cancel = () => {
      window.clearTimeout(timer.current);
      timer.current = undefined;
      setEpoch((e) => e + 1);
    };
    draftCancels.add(cancel);
    return () => {
      draftCancels.delete(cancel);
      window.clearTimeout(timer.current);
      timer.current = undefined;
    };
  }, []);

  const setDraft = useCallback(
    (next: T) => {
      setDraftState(next);
      window.clearTimeout(timer.current);
      timer.current = undefined;
      const normalized = latest.current.normalize?.(next) ?? next;
      const id = JSON.stringify(normalized);
      if (id === sent.current) return;
      const generation = navGeneration;
      timer.current = window.setTimeout(() => {
        timer.current = undefined;
        if (generation !== navGeneration) {
          setEpoch((e) => e + 1);
          return;
        }
        sent.current = id;
        latest.current.commit(normalized);
      }, delay);
    },
    [delay],
  );

  return [draft, setDraft] as const;
}
